import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import type { AdsHistoryGroupedFilterValues } from '../utils/adsHistoryGroupedFilters';
import { EMPTY_FILTERS, MOCK_FACEBOOK_USERS } from '../utils/adsHistoryGroupedFilters';
import type { AdsHistoryGroupedEntry } from './AdsHistoryGroupedView.mockData';

interface AdsHistoryGroupedFiltersProps {
  entries: AdsHistoryGroupedEntry[];
  filters: AdsHistoryGroupedFilterValues;
  onFilterChange: (next: AdsHistoryGroupedFilterValues) => void;
}

function uniqueSorted(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter(Boolean) as string[])).sort();
}

const HEIGHT = 36;
const inputSx = {
  height: HEIGHT,
  fontSize: '0.875rem',
  '& .MuiOutlinedInput-input': {
    padding: '0 14px',
    height: '100%',
    boxSizing: 'border-box',
    fontSize: '0.875rem',
  },
};
const inputInnerProps = { style: { fontSize: '0.875rem' } };

function renderPlaceholder(label: string) {
  return function SelectValue(value: unknown) {
    return value ? (
      <Typography
        variant="body2"
        noWrap
        sx={{ maxWidth: 160, display: 'block' }}
      >
        {String(value)}
      </Typography>
    ) : (
      <Typography variant="body2" color="text.disabled">{label}</Typography>
    );
  };
}

const FilterClearButton: FC<{ onClear: () => void }> = ({ onClear }) => (
  <IconButton
    size="small"
    aria-label="Clear filter"
    onClick={(e) => {
      e.stopPropagation();
      onClear();
    }}
    sx={{ mr: -0.5 }}
  >
    <ClearIcon fontSize="small" />
  </IconButton>
);

interface GroupedFilterSelectProps {
  placeholder: string;
  value: string;
  onChange: (e: SelectChangeEvent<unknown>) => void;
  onClear: () => void;
  minWidth: number;
  children: ReactNode;
}

const GroupedFilterSelect: FC<GroupedFilterSelectProps> = ({
  placeholder,
  value,
  onChange,
  onClear,
  minWidth,
  children,
}) => (
  <FormControl sx={{ minWidth }}>
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      renderValue={renderPlaceholder(placeholder)}
      sx={inputSx}
      IconComponent={value ? () => null : undefined}
      input={
        value ? (
          <OutlinedInput
            sx={inputSx}
            endAdornment={<FilterClearButton onClear={onClear} />}
          />
        ) : undefined
      }
    >
      {children}
    </Select>
  </FormControl>
);

interface GroupedFilterDateInputProps {
  value: string;
  label: string;
  minWidth: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

const GroupedFilterDateInput: FC<GroupedFilterDateInputProps> = ({
  value,
  label,
  minWidth,
  onChange,
  onClear,
}) => (
  <FormControl sx={{ minWidth }}>
    <OutlinedInput
      type="date"
      value={value}
      onChange={onChange}
      sx={inputSx}
      inputProps={inputInnerProps}
      startAdornment={
        <Box component="span" sx={{ mr: 0.5, color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
      }
      endAdornment={
        value ? (
          <InputAdornment position="end">
            <FilterClearButton onClear={onClear} />
          </InputAdornment>
        ) : undefined
      }
    />
  </FormControl>
);

const AdsHistoryGroupedFilters: FC<AdsHistoryGroupedFiltersProps> = ({
  entries,
  filters,
  onFilterChange,
}) => {
  const setSelect =
    (field: keyof AdsHistoryGroupedFilterValues) =>
    (e: SelectChangeEvent<unknown>) => {
      onFilterChange({ ...filters, [field]: e.target.value as string });
    };

  const setInput =
    (field: keyof AdsHistoryGroupedFilterValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({ ...filters, [field]: e.target.value });
    };

  const clearField =
    (field: keyof AdsHistoryGroupedFilterValues) => () => {
      onFilterChange({ ...filters, [field]: '' });
    };

  const flows = useMemo(() => uniqueSorted(entries.map((e) => e.flow)), [entries]);
  const hypotheses = useMemo(
    () => uniqueSorted(entries.map((e) => e.hypothesis)),
    [entries],
  );
  const customers = useMemo(
    () => uniqueSorted(entries.map((e) => e.customer)),
    [entries],
  );
  const technologies = useMemo(
    () => uniqueSorted(entries.map((e) => e.technology)),
    [entries],
  );
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(Boolean),
    [filters],
  );

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
      {/* User */}
      <GroupedFilterSelect
        placeholder="User"
        value={filters.user}
        onChange={setSelect('user')}
        onClear={clearField('user')}
        minWidth={160}
      >
        <MenuItem value="">All</MenuItem>
        {MOCK_FACEBOOK_USERS.map((u) => (
          <MenuItem key={u} value={u}>{u}</MenuItem>
        ))}
      </GroupedFilterSelect>

      {/* Date from */}
      <GroupedFilterDateInput
        label="From"
        value={filters.dateFrom}
        onChange={setInput('dateFrom')}
        onClear={clearField('dateFrom')}
        minWidth={155}
      />

      {/* Date to */}
      <GroupedFilterDateInput
        label="To"
        value={filters.dateTo}
        onChange={setInput('dateTo')}
        onClear={clearField('dateTo')}
        minWidth={140}
      />

      {/* Flow */}
      <GroupedFilterSelect
        placeholder="Flow"
        value={filters.flow}
        onChange={setSelect('flow')}
        onClear={clearField('flow')}
        minWidth={130}
      >
        <MenuItem value="">All</MenuItem>
        {flows.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
      </GroupedFilterSelect>

      {/* Hypothesis */}
      <GroupedFilterSelect
        placeholder="Hypothesis"
        value={filters.hypothesis}
        onChange={setSelect('hypothesis')}
        onClear={clearField('hypothesis')}
        minWidth={160}
      >
        <MenuItem value="">All</MenuItem>
        {hypotheses.map((h) => (
          <MenuItem key={h} value={h}>
            <Box component="span" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {h}
            </Box>
          </MenuItem>
        ))}
      </GroupedFilterSelect>

      {/* Customer */}
      <GroupedFilterSelect
        placeholder="Customer"
        value={filters.customer}
        onChange={setSelect('customer')}
        onClear={clearField('customer')}
        minWidth={130}
      >
        <MenuItem value="">All</MenuItem>
        {customers.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
      </GroupedFilterSelect>

      {/* Technology */}
      <GroupedFilterSelect
        placeholder="Technology"
        value={filters.technology}
        onChange={setSelect('technology')}
        onClear={clearField('technology')}
        minWidth={130}
      >
        <MenuItem value="">All</MenuItem>
        {technologies.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </GroupedFilterSelect>

      {/* Status */}
      <GroupedFilterSelect
        placeholder="Status"
        value={filters.status}
        onChange={setSelect('status')}
        onClear={clearField('status')}
        minWidth={120}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="success">Success</MenuItem>
        <MenuItem value="failed">Failed</MenuItem>
      </GroupedFilterSelect>

      {hasActiveFilters && (
        <Button
          variant="text"
          size="small"
          startIcon={<ClearIcon fontSize="small" />}
          onClick={() => { onFilterChange(EMPTY_FILTERS); }}
          sx={{
            height: HEIGHT,
            alignSelf: 'center',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            fontWeight: 'normal',
            textTransform: 'none',
          }}
        >
          Clear all
        </Button>
      )}
    </Box>
  );
};

export default AdsHistoryGroupedFilters;
